import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { LicenseService } from '@core/services';

@Component({
  selector: 'app-license-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './license-banner.component.html',
  styleUrls: ['./license-banner.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('300ms cubic-bezier(0.16, 1, 0.3, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class LicenseBannerComponent implements OnInit, OnDestroy {
  isVisible = false;
  isDismissed = false;
  daysLeft: number | null = null;
  isGrace = false;

  // Freemium usage banner
  isFreemium = false;
  meetingsUsed = 0;
  meetingLimit: number | null = null;

  private _sub?: Subscription;

  constructor(private readonly _licenseService: LicenseService) {}

  ngOnInit(): void {
    this._sub = this._licenseService.context$.subscribe(() => {
      this._refresh();
    });
    this._refresh();
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  get bannerText(): string {
    if (this.isGrace) return 'Your license has expired but you are still in the grace period.';
    if (this.daysLeft === 0) return 'Your license expires today.';
    if (this.daysLeft === 1) return 'Your license expires tomorrow.';
    return `Your license expires in ${this.daysLeft} days.`;
  }

  get freemiumText(): string {
    if (this.meetingLimit === null) {
      return `You're on the Free plan · ${this.meetingsUsed} meetings used this month.`;
    }
    return `You're on the Free plan · ${this.meetingsUsed} of ${this.meetingLimit} meetings used this month.`;
  }

  /** Usage as a 0–100 percentage for the progress bar (0 when unlimited). */
  get usagePercent(): number {
    if (!this.meetingLimit || this.meetingLimit <= 0) return 0;
    const pct = (this.meetingsUsed / this.meetingLimit) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  /** True once the freemium user has hit their meeting limit. */
  get isLimitReached(): boolean {
    return this.meetingLimit !== null && this.meetingsUsed >= this.meetingLimit;
  }

  dismiss(): void {
    this.isDismissed = true;
  }

  openContactUs(): void {
    window.open('https://convverse.ai/contact-us', '_blank', 'noopener,noreferrer');
  }

  private _refresh(): void {
    if (this.isDismissed) return;

    // Freemium usage takes priority for freemium users.
    this.isFreemium = this._licenseService.isFreemium;
    if (this.isFreemium) {
      this.meetingsUsed = this._licenseService.meetingsUsed ?? 0;
      this.meetingLimit = this._licenseService.meetingLimit;
      this.isVisible = true;
      return;
    }

    this.daysLeft = this._licenseService.daysUntilExpiry;
    this.isGrace = this._licenseService.effectiveState === 'in_grace';
    this.isVisible = this._licenseService.showExpiryWarning || this.isGrace;
  }
}
